import { Component, ElementRef, OnInit, ViewChild, HostListener } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { VideosService } from '../../../core/services/videos.service';
import { Video, VideoCategory } from '../../../core/models/video.model';
import { PapersService, Paper, CreatePaperDto } from '../../../core/services/papers.service';
import { TestsService } from '../../../core/services/tests.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { USER_TYPES } from '../../../core/shared/constants';
import { UsersService } from '../../../core/services/users.service';
import { BaseResponse } from '../../../core/models/base.response';
import { User } from '../../../core/models/user.interface';
import { VisionboardService } from '../../../core/services/visionboard.service';

type AcademyDimension = { id: string; name: string; color: string };

type PaperPreview = Paper & {
  authorName?: string;
  learningPoints?: string[];
};

@Component({
  selector: 'app-academy',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxSpinnerModule, DecimalPipe],
  templateUrl: './academy.component.html',
  styleUrls: ['./academy.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(10px)' }))
      ])
    ])
  ]
})
export class AcademyComponent implements OnInit {
  // Tab principal - Lecturas o Videos
  activeMainTab: 'lecturas' | 'videos' = 'lecturas';
  
  searchTerm: string = '';
  activeFilter: string = 'todos';
  
  // Variable para controlar si los datos están cargados
  private dataLoaded = {
    papers: false,
    videos: false,
    categories: false,
    dimensions: false
  };
  private pendingDimension: string | null = null;
  
  // Mapeo dinámico de dimensiones cargado desde la base de datos
  private dimensionToCategoryMap: { [key: string]: string } = {};
  
  // Dimensiones cargadas desde la base de datos
  private loadedDimensions: AcademyDimension[] = [];
  
  // Papers (Lecturas)
  allPapers: Paper[] = [];
  featuredPapers: Paper[] = [];
  filteredPapers: Paper[] = [];
  
  // Videos
  allVideos: Video[] = [];
  featuredVideos: Video[] = [];
  filteredVideos: Video[] = [];
  
  // Categorías para filtros
  categories: {id: string, name: string, count: number}[] = [
    { id: 'todos', name: 'todo', count: 0 },
    { id: 'destacados', name: 'destacados', count: 0 }
  ];
  
  // Categorías de videos y papers
  videoCategories: VideoCategory[] = [];
  paperCategories: any[] = [];
  
  // Paginación
  currentPage = 1;
  itemsPerPage = 8;
  totalItems = 0;
  totalPages = 0;
  paginatedItems: Paper[] | Video[] = [];

  // Propiedades para vista de gestión (mentores/expertos)
  isManagementView: boolean = false;
  user: any = null;

  // Papers para gestión
  managementPapers: Paper[] = [];
  managementFilteredPapers: Paper[] = [];
  searchTitle: string = '';
  statusFilter: string = '';
  dimensionFilter: string = '';
  dimensions: AcademyDimension[] = [];
  activeExperts: User[] = [];
  
  // Categorías de papers para gestión (cargadas dinámicamente desde el backend)
  managementPaperCategories: AcademyDimension[] = [];
  
  // Paginación para gestión
  currentPageManagement: number = 1;
  itemsPerPageManagement: number = 10;
  totalItemsManagement: number = 0;
  totalPagesManagement: number = 0;
  
  // Modal de formulario
  showDocumentModal: boolean = false;
  paperForm: {
    title: string;
    description: string;
    author: string;
    category: string; // Mantener para compatibilidad con backend (dimensiones separadas por comas)
    dimension: string; // Mantener para compatibilidad temporal
    dimensions: string[]; // Array de IDs de dimensiones seleccionadas
    coverUrl: string;
    fileUrl: string;
    pages: number;
    price: number;
    isFree: boolean;
    featured: boolean;
    tags: string[];
    learningPoints: string[]; // 3 campos separados
    publishedDate: string | Date;
    downloads: number;
    content: string;
    summary: string;
    keywords: string;
    language: string;
    isbn: string;
    publisher: string;
    year: number;
    status: number; // 1 = activo, 2 = inactivo, 3 = En revision, 4 = Borrador
  } = {
    title: '',
    description: '',
    author: '',
    category: '',
    dimension: '',
    dimensions: [],
    coverUrl: '',
    fileUrl: '',
    pages: 1,
    price: 0,
    isFree: false,
    featured: false,
    tags: ['', '', '', '', '', ''], // 6 campos separados
    learningPoints: ['', '', ''], // 3 campos separados
    publishedDate: '',
    downloads: 0,
    content: '',
    summary: '',
    keywords: '',
    language: 'es',
    isbn: '',
    publisher: '',
    year: new Date().getFullYear(),
    status: 4 // Por defecto: Borrador
  };
  
  // Propiedades para el multiselect de dimensiones
  dimensionDropdownOpen: boolean = false;
  dimensionError: string = '';
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const multiselectContainer = target.closest('.multiselect-container');
    if (!multiselectContainer && this.dimensionDropdownOpen) {
      this.dimensionDropdownOpen = false;
    }
  }
  isEditMode: boolean = false;
  editingPaperId: string | null = null;
  selectedPdfFile: File | null = null;
  selectedCoverFile: File | null = null;
  @ViewChild('pdfFileInput') pdfFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('coverFileInput') coverFileInput?: ElementRef<HTMLInputElement>;

  showPaperPreview: boolean = false;
  paperPreviewData: PaperPreview | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private videosService: VideosService,
    private papersService: PapersService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute,
    private testsService: TestsService,
    private authService: AuthService,
    private usersService: UsersService
  ) {}

  ngOnInit(): void {
    // Verificar si es mentor o experto
    this.user = this.authService.getUser();
    
    // Verificar si se fuerza la vista de cliente desde la ruta
    const forceCustomerView = this.route.snapshot.data['forceCustomerView'];
    
    // Si forceCustomerView es true, mostrar vista de cliente aunque sea mentor/experto
    if (forceCustomerView) {
      this.isManagementView = false;
    } else {
      this.isManagementView = this.isMentorOrExpert();
    }
    
    if (this.isManagementView) {
      // Cargar vista de gestión
      this.loadManagementData();
    } else {
      // Cargar vista normal de Academy
      this.spinner.show();
      this.loadDimensions();
      this.loadCategories();
      this.loadPapers();
      this.loadVideos();
      
      // Escuchar cambios en los query parameters
      this.route.queryParams.subscribe(params => {
        const dimension = params['dimension'];
        console.log('Dimensión recibida:', dimension);
        if (dimension) {
          this.pendingDimension = dimension;
          this.checkAndActivatePendingDimension();
        }
      });
    }
  }

  isMentorOrExpert(): boolean {
    if (!this.user || !this.user.type) return false;
    
    // Verificar si es Coach (id: 2) o Expert (id: 4)
    return this.user.type.id === USER_TYPES.Coach.id || 
           this.user.type.id === USER_TYPES.Expert.id;
  }

  loadManagementData(): void {
    this.spinner.show();
    // Cargar dimensiones para el filtro
    this.testsService.getDimensions().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          const normalizedDimensions = this.normalizeDimensions(response.data);
          this.dimensions = normalizedDimensions;
          this.managementPaperCategories = [...normalizedDimensions];
          this.systemCategories = normalizedDimensions.map(dim => ({
            id: dim.id,
            name: dim.name,
            color: this.getDefaultCategoryColor(dim.id)
          }));

          this.dimensionToCategoryMap = {};
          normalizedDimensions.forEach(dim => {
            this.dimensionToCategoryMap[dim.name] = dim.id;
          });
        }
      },
      error: (err: any) => {
        console.error('Error al cargar dimensiones:', err);
      }
    });
    
    // Cargar expertos activos primero, luego papers
    this.loadActiveExperts();
    
    // Cargar papers
    this.loadManagementPapers();
  }

  loadActiveExperts(): void {
    this.usersService.findAll().subscribe({
      next: (response: BaseResponse<User[]>) => {
        const users = response.data || [];
        // Filtrar expertos (tipo 4 = Experto) y mentores (tipo 2 = Coach)
        this.activeExperts = users.filter(user => 
          user.type && (user.type.id === USER_TYPES.Expert.id || user.type.id === USER_TYPES.Coach.id)
        );
        
        // Asegurar que el usuario actual esté en la lista si no está
        const currentUserId = this.user?._id;
        if (currentUserId && !this.activeExperts.find(exp => exp._id === currentUserId)) {
          // Si el usuario actual no está en la lista, agregarlo
          if (this.user) {
            this.activeExperts.unshift(this.user);
          }
        }
      },
      error: (err: any) => {
        console.error('Error al cargar expertos:', err);
        this.activeExperts = [];
        // Si hay error, al menos asegurar que el usuario actual esté disponible
        if (this.user) {
          this.activeExperts = [this.user];
        }
      }
    });
  }

  loadManagementPapers(): void {
    // Obtener el ID del usuario actual
    const currentUserId = this.user?._id;
    
    if (!currentUserId) {
      console.error('No se pudo obtener el ID del usuario actual');
      this.managementPapers = [];
      this.managementFilteredPapers = [];
      this.spinner.hide();
      Swal.fire({
        title: 'Error',
        text: 'No se pudo identificar al usuario actual',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    this.papersService.getPapers().subscribe({
      next: (papers: Paper[]) => {
        // Filtrar solo los papers del usuario actual
        const allPapers = Array.isArray(papers) ? papers : [];
        this.managementPapers = allPapers.filter(paper => {
          // Comparar el campo author con el ID del usuario actual
          return paper.author === currentUserId;
        });
        
        console.log(`Papers del usuario ${currentUserId}:`, this.managementPapers.length);
        this.applyFilters();
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error('Error al cargar papers', err);
        this.managementPapers = [];
        this.managementFilteredPapers = [];
        this.spinner.hide();
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los papers',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  applyFilters(): void {
    if (!Array.isArray(this.managementPapers)) {
      this.managementPapers = [];
    }
    
    this.managementFilteredPapers = this.managementPapers.filter(paper => {
      const matchesTitle = !this.searchTitle || 
        paper.title.toLowerCase().includes(this.searchTitle.toLowerCase()) ||
        paper.description.toLowerCase().includes(this.searchTitle.toLowerCase());
      
      // Mapear estado: "Publicado" si tiene publishedDate, "Borrador" si no
      const paperStatus = this.getPaperStatus(paper);
      const matchesStatus = !this.statusFilter || 
        paperStatus.toLowerCase() === this.statusFilter.toLowerCase();
      
      // Mapear categoría a dimensión
      const matchesDimension = !this.dimensionFilter || 
        this.getPaperDimensionName(paper.category) === this.dimensionFilter;
      
      return matchesTitle && matchesStatus && matchesDimension;
    });
    
    this.totalItemsManagement = this.managementFilteredPapers.length;
    this.totalPagesManagement = Math.ceil(this.totalItemsManagement / this.itemsPerPageManagement);
    this.currentPageManagement = 1;
  }

  getPaperStatus(paper: Paper): string {
    // Mapear el campo status numérico a texto
    if (!paper.status) {
      return 'Borrador'; // Por defecto si no tiene status
    }
    
    switch (paper.status) {
      case 1:
        return 'Activo';
      case 2:
        return 'Inactivo';
      case 3:
        return 'En revision';
      case 4:
        return 'Borrador';
      default:
        return 'Borrador';
    }
  }
  
  getPaperStatusClass(paper: Paper): string {
    // Retornar clase CSS según el estado
    if (!paper.status) {
      return 'draft';
    }
    
    switch (paper.status) {
      case 1:
        return 'active';
      case 2:
        return 'inactive';
      case 3:
        return 'review';
      case 4:
        return 'draft';
      default:
        return 'draft';
    }
  }

  getPaperDimensionName(category: any): string {
    if (!category) return '';
    
    // Si es un array de objetos {id, name}
    if (Array.isArray(category)) {
      if (category.length === 0) return '';
      
      const categoryNames = category.map((cat: any) => {
        if (typeof cat === 'object' && cat.name) {
          return cat.name;
        }
        if (typeof cat === 'object' && cat.id) {
          const foundCategory = this.managementPaperCategories.find(c => c.id === cat.id);
          return foundCategory ? foundCategory.name : cat.id;
        }
        if (typeof cat === 'string') {
          const foundCategory = this.managementPaperCategories.find(c => c.id === cat);
          return foundCategory ? foundCategory.name : cat;
        }
        return '';
      }).filter((name: string) => name.length > 0);
      
      return categoryNames.join(', ');
    }
    
    // Si es un string (formato antiguo para compatibilidad)
    if (typeof category === 'string') {
      // Si contiene comas, es múltiples dimensiones
      if (category.includes(',')) {
        const categoryIds = category.split(',').map(id => id.trim()).filter(id => id.length > 0);
        const categoryNames = categoryIds.map(id => {
          const foundCategory = this.managementPaperCategories.find(cat => cat.id === id);
          return foundCategory ? foundCategory.name : id;
        });
        return categoryNames.join(', ');
      }
      
      // Dimensión única
      const foundCategory = this.managementPaperCategories.find(cat => cat.id === category);
      return foundCategory ? foundCategory.name : category;
    }
    
    return '';
  }
  
  getPaperDimensionsArray(category: any): string[] {
    // Usar parseCategoryString que ya maneja ambos formatos
    return this.parseCategoryString(category);
  }
  
  getDimensionNameById(dimensionId: string): string {
    const dimension = this.dimensions.find(dim => dim.id === dimensionId);
    if (dimension) return dimension.name;
    
    // Buscar también en managementPaperCategories
    const category = this.managementPaperCategories.find(cat => cat.id === dimensionId);
    return category ? category.name : dimensionId;
  }
  
  toggleDimensionDropdown(): void {
    this.dimensionDropdownOpen = !this.dimensionDropdownOpen;
    this.dimensionError = '';
  }
  
  isDimensionSelected(dimensionId: string): boolean {
    return this.paperForm.dimensions && this.paperForm.dimensions.includes(dimensionId);
  }
  
  toggleDimension(dimensionId: string): void {
    if (!this.paperForm.dimensions) {
      this.paperForm.dimensions = [];
    }
    
    const index = this.paperForm.dimensions.indexOf(dimensionId);
    
    if (index > -1) {
      // Remover dimensión
      this.paperForm.dimensions.splice(index, 1);
    } else {
      // Agregar dimensión (validar máximo 4)
      if (this.paperForm.dimensions.length >= 4) {
        this.dimensionError = 'Solo puedes seleccionar un máximo de 4 dimensiones';
        return;
      }
      this.paperForm.dimensions.push(dimensionId);
      this.dimensionError = '';
    }
    
    // Actualizar category para compatibilidad con backend
    this.updateCategoryString();
    
    // Cerrar dropdown si se alcanza el máximo
    if (this.paperForm.dimensions.length >= 4) {
      this.dimensionDropdownOpen = false;
    }
  }
  
  removeDimension(index: number): void {
    if (this.paperForm.dimensions && index >= 0 && index < this.paperForm.dimensions.length) {
      this.paperForm.dimensions.splice(index, 1);
      this.updateCategoryString();
      this.dimensionError = '';
    }
  }
  
  updateCategoryString(): void {
    // Convertir array de dimensiones a string (separado por comas) para el backend
    if (this.paperForm.dimensions && this.paperForm.dimensions.length > 0) {
      this.paperForm.category = this.paperForm.dimensions.join(',');
    } else {
      this.paperForm.category = '';
    }
  }
  
  parseCategoryString(category: any): string[] {
    if (!category) return [];
    
    // Si es un array de objetos {id, name}
    if (Array.isArray(category)) {
      return category.map((cat: any) => {
        if (typeof cat === 'object' && cat.id) {
          return cat.id;
        }
        return typeof cat === 'string' ? cat : '';
      }).filter((id: string) => id.length > 0);
    }
    
    // Si es un string separado por comas (formato antiguo para compatibilidad)
    if (typeof category === 'string') {
      return category.split(',').map(cat => cat.trim()).filter(cat => cat.length > 0);
    }
    
    return [];
  }

  trackByLearningPointIndex(index: number, item: any): number {
    return index;
  }

  onLearningPointChange(index: number, value: string): void {
    if (this.paperForm.learningPoints && this.paperForm.learningPoints.length > index) {
      this.paperForm.learningPoints[index] = value;
    }
  }

  trackByTagIndex(index: number, item: any): number {
    return index;
  }

  onTagChange(index: number, value: string): void {
    if (this.paperForm.tags && this.paperForm.tags.length > index) {
      this.paperForm.tags[index] = value;
    }
  }

  getExpertName(expertId: string): string {
    const expert = this.activeExperts.find(exp => exp._id === expertId);
    return expert ? `${expert.firstName} ${expert.lastName}` : expertId;
  }

  filterDocuments(): void {
    this.applyFilters();
  }

  getPaginatedDocuments(): Paper[] {
    const start = (this.currentPageManagement - 1) * this.itemsPerPageManagement;
    const end = start + this.itemsPerPageManagement;
    return this.managementFilteredPapers.slice(start, end);
  }

  getPageNumbersManagement(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(10, this.totalPagesManagement);
    for (let i = 1; i <= maxPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPageManagement(page: number): void {
    if (page >= 1 && page <= this.totalPagesManagement) {
      this.currentPageManagement = page;
    }
  }

  previousPageManagement(): void {
    if (this.currentPageManagement > 1) {
      this.currentPageManagement--;
    }
  }

  nextPageManagement(): void {
    if (this.currentPageManagement < this.totalPagesManagement) {
      this.currentPageManagement++;
    }
  }

  changeItemsPerPageManagement(count: number): void {
    this.itemsPerPageManagement = count;
    this.totalPagesManagement = Math.ceil(this.totalItemsManagement / this.itemsPerPageManagement);
    this.currentPageManagement = 1;
  }

  getUserDisplayName(): string {
    if (!this.user) return '';
    return `${this.user.firstName || ''} ${this.user.lastName || ''}`.trim();
  }

  getUserProfession(): string {
    if (!this.user || !this.user.coachProfileData) return '';
    return this.user.coachProfileData.professionalTitle || '';
  }

  onDimensionChange(): void {
    // Esta función ya no se usa directamente, pero se mantiene para compatibilidad
    // El multiselect actualiza directamente paperForm.dimensions y category
    this.updateCategoryString();
  }

  openDocumentModal(paper?: Paper): void {
    if (paper) {
      this.isEditMode = true;
      this.editingPaperId = paper._id || null;
      
      // Convertir arrays del paper a arrays para el formulario
      let tagsArray: string[] = [];
      if (Array.isArray(paper.tags)) {
        tagsArray = paper.tags;
      } else if (paper.tags && typeof paper.tags === 'string') {
        tagsArray = (paper.tags as string).split(',').map((t: string) => t.trim());
      }
      
      let learningObjectivesArray: string[] = [];
      if (Array.isArray(paper.learningObjectives)) {
        learningObjectivesArray = paper.learningObjectives;
      } else if (paper.learningObjectives && typeof paper.learningObjectives === 'string') {
        learningObjectivesArray = (paper.learningObjectives as string).split('\n').map((l: string) => l.trim());
      }
      
      // Llenar arrays con los valores del paper, completando con strings vacíos
      const tagsForm = [...tagsArray.slice(0, 6)];
      while (tagsForm.length < 6) tagsForm.push('');
      
      const learningPointsForm = [...learningObjectivesArray.slice(0, 3)];
      while (learningPointsForm.length < 3) learningPointsForm.push('');
      
      // Obtener dimensiones desde la categoría
      const dimensionName = this.getPaperDimensionName(paper.category);
      const dimensionsArray = this.parseCategoryString(paper.category);
      
      this.paperForm = {
        title: paper.title,
        description: paper.description,
        author: paper.author,
        category: paper.category,
        dimension: dimensionName,
        dimensions: dimensionsArray,
        coverUrl: paper.coverUrl || '',
        fileUrl: paper.fileUrl,
        pages: paper.pages,
        price: paper.price,
        isFree: paper.isFree,
        featured: paper.featured,
        tags: tagsForm,
        learningPoints: learningPointsForm,
        publishedDate: paper.publishedDate ? new Date(paper.publishedDate).toISOString().split('T')[0] : '',
        downloads: paper.downloads,
        content: paper.content || '',
        summary: paper.summary || '',
        keywords: this.parseArrayField(paper.keywords),
        language: paper.language,
        isbn: paper.isbn || '',
        publisher: paper.publisher || '',
        year: paper.year,
        status: paper.status || 4
      };
      this.selectedPdfFile = null;
      this.selectedCoverFile = null;
    } else {
      this.isEditMode = false;
      this.editingPaperId = null;
      this.selectedPdfFile = null;
      this.selectedCoverFile = null;
      this.dimensionDropdownOpen = false;
      this.dimensionError = '';
      // El autor se establecerá automáticamente al guardar
      this.paperForm = {
        title: '',
        description: '',
        author: '', // Se establecerá automáticamente al guardar
        category: '',
        dimension: '',
        dimensions: [],
        coverUrl: '',
        fileUrl: '',
        pages: 1,
        price: 0,
        isFree: false,
        featured: false,
        tags: ['', '', '', '', '', ''],
        learningPoints: ['', '', ''],
        publishedDate: '',
        downloads: 0,
        content: '',
        summary: '',
        keywords: '',
        language: 'es',
        isbn: '',
        publisher: '',
        year: new Date().getFullYear(),
        status: 4 // Por defecto: Borrador
      };
    }
    this.showDocumentModal = true;
  }

  closeDocumentModal(): void {
    this.showDocumentModal = false;
    this.isEditMode = false;
    this.editingPaperId = null;
    this.selectedPdfFile = null;
    this.selectedCoverFile = null;
  }

  private parseArrayField(field: any, separator: string = ', '): string {
    if (!field) {
      return '';
    }
    
    if (Array.isArray(field)) {
      return field.join(separator);
    }
    
    if (typeof field === 'string') {
      const cleanField = field.trim();
      if (cleanField.startsWith('[') && cleanField.endsWith(']')) {
        try {
          const parsed = JSON.parse(cleanField);
          if (Array.isArray(parsed)) {
            return parsed.join(separator);
          }
        } catch (error) {
          const content = cleanField.slice(1, -1);
          const items = content.split(',').map(item => {
            const trimmed = item.trim();
            if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
                (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
              return trimmed.slice(1, -1);
            }
            return trimmed;
          }).filter(item => item.length > 0);
          return items.join(separator);
        }
      }
      return field;
    }
    
    return '';
  }

  onPaperFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        Swal.fire({
          title: 'Error',
          text: 'Por favor selecciona un archivo PDF válido',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        return;
      }
      this.selectedPdfFile = file;
    }
  }

  onPaperCoverSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          title: 'Error',
          text: 'Por favor selecciona un archivo de imagen válido',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        return;
      }
      this.selectedCoverFile = file;
      // Subir la imagen de portada al servidor
      this.spinner.show();
      this.papersService.uploadCover(file).subscribe({
        next: (response) => {
          this.spinner.hide();
          this.paperForm.coverUrl = response.coverUrl;
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al subir portada:', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo subir la imagen de portada',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    }
  }

  triggerPdfUpload(): void {
    this.pdfFileInput?.nativeElement.click();
  }

  triggerCoverUpload(): void {
    this.coverFileInput?.nativeElement.click();
  }

  saveAsDraft(): void {
    // Guardar sin publishedDate y con estado Borrador (4)
    this.paperForm.publishedDate = '';
    this.paperForm.status = 4; // Borrador
    this.saveManagementPaper();
    this.loadManagementPapers();
  }

  requestReview(): void {
    // Guardar con publishedDate y estado En revision (3)
    if (!this.paperForm.publishedDate) {
      this.paperForm.publishedDate = new Date().toISOString().split('T')[0];
    }
    this.paperForm.status = 3; // En revision
    this.saveManagementPaper();
    this.loadManagementPapers();
  }

  editDocument(paper: Paper): void {
    this.openDocumentModal(paper);
  }

  deleteDocument(paper: Paper): void {
    if (paper._id) {
      this.disableManagementPaper(paper._id);
    }
  }

  disableDocument(paper: Paper): void {
    if (paper._id) {
      this.disableManagementPaper(paper._id);
    }
  }

  saveManagementPaper(): void {
    // Establecer automáticamente el autor como el usuario actual de la sesión
    const currentUserId = this.user?._id;
    if (!currentUserId) {
      Swal.fire({
        title: 'Error',
        text: 'No se pudo identificar al usuario actual',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }
    
    // Asignar automáticamente el autor
    this.paperForm.author = currentUserId;
    
    // Actualizar category desde dimensions si es necesario
    if (this.paperForm.dimensions && this.paperForm.dimensions.length > 0 && !this.paperForm.category) {
      this.updateCategoryString();
    }
    
    // Validar que haya al menos una dimensión seleccionada
    if (!this.paperForm.dimensions || this.paperForm.dimensions.length === 0) {
      this.dimensionError = 'Debes seleccionar al menos una dimensión';
      Swal.fire({
        title: 'Error',
        text: 'Por favor selecciona al menos una dimensión',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }
    
    // Validar que no exceda el máximo de 4 dimensiones
    if (this.paperForm.dimensions.length > 4) {
      this.dimensionError = 'Solo puedes seleccionar un máximo de 4 dimensiones';
      Swal.fire({
        title: 'Error',
        text: 'Solo puedes seleccionar un máximo de 4 dimensiones',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }
    
    // Actualizar category string antes de guardar
    this.updateCategoryString();
    
    // Valores por defecto para campos que no están en el formulario simplificado
    if (!this.paperForm.pages) this.paperForm.pages = 1;
    if (!this.paperForm.price) this.paperForm.price = 0;
    if (this.paperForm.isFree === undefined) this.paperForm.isFree = false;
    if (!this.paperForm.language) this.paperForm.language = 'es';
    if (!this.paperForm.year) this.paperForm.year = new Date().getFullYear();
    
    if (!this.paperForm.title || !this.paperForm.description || !this.paperForm.category) {
      Swal.fire({
        title: 'Error',
        text: 'Por favor completa todos los campos requeridos',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    if (!this.isEditMode && !this.selectedPdfFile) {
      Swal.fire({
        title: 'Error',
        text: 'Para crear un paper necesitas subir un archivo PDF',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    if (this.selectedCoverFile && !this.paperForm.coverUrl) {
      Swal.fire({
        title: 'Procesando',
        text: 'Subiendo imagen de portada...',
        icon: 'info',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      this.papersService.uploadCover(this.selectedCoverFile).subscribe({
        next: (response) => {
          this.paperForm.coverUrl = response.coverUrl;
          Swal.close();
          this.continueWithSavePaper();
        },
        error: (err: any) => {
          console.error('Error al subir portada:', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo subir la imagen de portada',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
      return;
    }

    this.continueWithSavePaper();
  }

  private continueWithSavePaper(): void {
    this.spinner.show();
    
    // Asegurar que el autor siempre sea el usuario actual de la sesión
    const currentUserId = this.user?._id;
    if (!currentUserId) {
      this.spinner.hide();
      Swal.fire({
        title: 'Error',
        text: 'No se pudo identificar al usuario actual',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }
    
    // Establecer automáticamente el autor como el usuario actual
    this.paperForm.author = currentUserId;
    
    // Convertir arrays de tags y learningPoints a formatos esperados
    const tags: string[] = Array.isArray(this.paperForm.tags) 
      ? this.paperForm.tags.filter((tag: string) => tag && tag.trim().length > 0)
      : [];
    
    const learningObjectives: string[] = Array.isArray(this.paperForm.learningPoints)
      ? this.paperForm.learningPoints.filter((point: string) => point && point.trim().length > 0)
      : [];
    
    const keywords = typeof this.paperForm.keywords === 'string'
      ? this.paperForm.keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword.length > 0)
      : [];
    
    // Convertir array de IDs de dimensiones a array de objetos {id, name} para el nuevo campo categories
    const categories = this.paperForm.dimensions && this.paperForm.dimensions.length > 0
      ? this.paperForm.dimensions.map(dimensionId => {
          const dimensionName = this.getDimensionNameById(dimensionId);
          return {
            id: dimensionId,
            name: dimensionName
          };
        })
      : [];
    
    // Mantener category como string (dimensiones separadas por comas) para compatibilidad
    const categoryString = this.paperForm.dimensions && this.paperForm.dimensions.length > 0
      ? this.paperForm.dimensions.join(',')
      : '';
    
    const paperData: CreatePaperDto = {
      title: this.paperForm.title,
      description: this.paperForm.description,
      author: currentUserId, // Siempre usar el usuario actual de la sesión
      category: categoryString,
      categories: categories.length > 0 ? categories : undefined,
      coverUrl: this.paperForm.coverUrl || undefined,
      fileUrl: this.paperForm.fileUrl || '',
      pages: this.paperForm.pages || 1,
      price: this.paperForm.price || 0,
      isFree: this.paperForm.isFree || false,
      featured: this.isEditMode ? this.paperForm.featured : false, // Mantener el valor original al editar, false al crear
      tags: tags,
      learningObjectives: learningObjectives,
      publishedDate: this.paperForm.publishedDate ? new Date(this.paperForm.publishedDate).toISOString() : undefined,
      content: this.paperForm.content || undefined,
      summary: this.paperForm.summary || undefined,
      keywords: keywords,
      language: this.paperForm.language || 'es',
      isbn: this.paperForm.isbn || undefined,
      publisher: this.paperForm.publisher || undefined,
      year: this.paperForm.year || new Date().getFullYear(),
      status: this.paperForm.status || 4 // Por defecto: Borrador
    };

    if (this.isEditMode && this.editingPaperId) {
      this.papersService.updatePaper(this.editingPaperId, paperData, this.selectedPdfFile || undefined).subscribe({
        next: () => {
          this.spinner.hide();
          Swal.fire({
            title: 'Éxito',
            text: 'Paper actualizado correctamente',
            icon: 'success',
            confirmButtonText: 'Ok'
          });
          this.closeDocumentModal();
          this.loadManagementPapers();
        },
        error: (err: any) => {
          this.spinner.hide();
          console.error('Error al actualizar paper', err);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo actualizar el paper',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      });
    } else {
      if (this.selectedPdfFile) {
        this.papersService.createPaper(paperData, this.selectedPdfFile).subscribe({
          next: () => {
            this.spinner.hide();
            Swal.fire({
              title: 'Éxito',
              text: 'Paper creado correctamente',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            this.closeDocumentModal();
            this.loadPapers();
          },
          error: (err: any) => {
            this.spinner.hide();
            console.error('Error al crear paper', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo crear el paper',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Para crear un paper necesitas subir un archivo PDF',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        this.spinner.hide();
      }
    }
  }

  deleteManagementPaper(paperId: string): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#CE0024',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.papersService.deletePaper(paperId).subscribe({
          next: () => {
            this.spinner.hide();
            Swal.fire({
              title: 'Éxito',
              text: 'Paper eliminado correctamente',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            this.loadPapers();
          },
          error: (err: any) => {
            this.spinner.hide();
            console.error('Error al eliminar paper', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo eliminar el paper',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      }
    });
  }

  disableManagementPaper(paperId: string): void {
    Swal.fire({
      title: '¿Inhabilitar paper?',
      text: 'El paper se marcará como inactivo y no será visible para los usuarios',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#CE0024',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, inhabilitar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        const updateData = { status: 2 }; // 2 = inactivo
        this.papersService.updatePaper(paperId, updateData).subscribe({
          next: () => {
            this.spinner.hide();
            Swal.fire({
              title: 'Éxito',
              text: 'Paper inhabilitado correctamente',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            this.loadManagementPapers();
          },
          error: (err: any) => {
            this.spinner.hide();
            console.error('Error al inhabilitar paper', err);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo inhabilitar el paper',
              icon: 'error',
              confirmButtonText: 'Ok'
            });
          }
        });
      }
    });
  }

  getPaperCoverUrl(coverUrl: string | undefined): string {
    if (!coverUrl || coverUrl === '' || coverUrl === 'null' || coverUrl === 'undefined') {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg4MFY4MEgyMFYyMFoiIGZpbGw9IiNEN0Q3RDciLz4KPC9zdmc+';
    }
    
    const cleanUrl = coverUrl.trim();
    if (cleanUrl.startsWith('http')) {
      return cleanUrl;
    }
    
    if (!environment.apiUrlClean) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg4MFY4MEgyMFYyMFoiIGZpbGw9IiNEN0Q3RDciLz4KPC9zdmc+';
    }
    
    const baseUrl = environment.apiUrlClean;
    return `${baseUrl}/${cleanUrl}`;
  }

  getPaperFileUrl(fileUrl: string | undefined): string {
    if (!fileUrl || fileUrl.trim() === '') {
      return '#';
    }

    const cleanUrl = fileUrl.trim();
    if (cleanUrl.startsWith('http')) {
      return cleanUrl;
    }

    if (!environment.apiUrlClean) {
      return '#';
    }

    const baseUrl = environment.apiUrlClean;
    return `${baseUrl}/${cleanUrl.replace(/^\/+/, '')}`;
  }

  loadDimensions(): void {
    this.testsService.getDimensions().subscribe({
      next: (response: any) => {
        console.log('Dimensiones cargadas desde BD:', response);
        if (response && response.data) {
          const normalizedDimensions = this.normalizeDimensions(response.data);
          this.loadedDimensions = normalizedDimensions;
          this.dimensions = normalizedDimensions;

          this.dimensionToCategoryMap = {};
          normalizedDimensions.forEach((dimension: any) => {
            if (dimension.name && dimension.id) {
              this.dimensionToCategoryMap[dimension.name] = dimension.id;
            }
          });

          this.managementPaperCategories = [...normalizedDimensions];
          this.systemCategories = normalizedDimensions.map(dim => ({
            id: dim.id,
            name: dim.name,
            color: this.getDefaultCategoryColor(dim.id)
          }));
          console.log('Dimensiones cargadas:', this.loadedDimensions);
          console.log('Mapeo de dimensiones construido:', this.dimensionToCategoryMap);
        }
        this.dataLoaded.dimensions = true;
        // Actualizar categorías después de cargar dimensiones
        this.updateCategoriesFilter();
        this.checkAndActivatePendingDimension();
      },
      error: (err: any) => {
        console.error('Error al cargar dimensiones desde BD:', err);
        this.clearDimensionsData();
        this.dataLoaded.dimensions = true;
        // Actualizar categorías después del fallback
        this.updateCategoriesFilter();
        this.checkAndActivatePendingDimension();
      }
    });
  }

  private clearDimensionsData(): void {
    console.warn('No se pudieron cargar dimensiones desde la base de datos. Limpiando datos locales.');
    this.loadedDimensions = [];
    this.dimensions = [];
    this.dimensionToCategoryMap = {};
    this.managementPaperCategories = [];
    this.systemCategories = [];
  }

  loadCategories(): void {
    this.videosService.getVideoCategories().subscribe({
      next: (categories: VideoCategory[]) => {
        console.log('Categorias cargadas desde BD:', categories);
        this.videoCategories = categories;
        this.updateCategoriesFilter();
        this.dataLoaded.categories = true;
        this.checkAndActivatePendingDimension();
      },
      error: (err: any) => {
        console.error('Error al cargar categorías', err);
      }
    });
  }

  updateCategoriesFilter(): void {
    console.log('Actualizando filtros de categorías...');
    console.log('Estado de carga:', this.dataLoaded);
    console.log('Dimensiones disponibles:', this.loadedDimensions);
    
    // Solo actualizar si tenemos las dimensiones cargadas
    if (!this.dataLoaded.dimensions || this.loadedDimensions.length === 0) {
      console.log('Dimensiones no cargadas aún, saltando actualización de categorías');
      return;
    }
    
    this.categories = [
      { id: 'todos', name: 'todo', count: this.getTotalCount() },
      { id: 'destacados', name: 'destacados', count: this.getFeaturedCount() }
    ];
    
    // Usar las dimensiones cargadas desde la base de datos
    this.loadedDimensions.forEach(dimension => {
      if (dimension.id) {
        const count = this.getCategoryCountById(dimension.id);
        console.log(`Dimensión: ${dimension.name} (ID: ${dimension.id}) - Count: ${count}`);
        // Agregar categoría aunque tenga count 0 para mostrarla en la UI
        this.categories.push({
          id: dimension.id,
          name: dimension.name,
          count: count
        });
      }
    });
    
    console.log('Categorías finales:', this.categories);
  }

  getTotalCount(): number {
    if (this.activeMainTab === 'lecturas') {
      return this.allPapers.length;
    } else {
      return this.allVideos.length;
    }
  }

  getFeaturedCount(): number {
    if (this.activeMainTab === 'lecturas') {
      return this.allPapers.filter(paper => paper.featured).length;
    } else {
      return this.allVideos.filter(video => video.featured).length;
    }
  }

  getCategoryCountById(categoryId: string): number {
    if (this.activeMainTab === 'lecturas') {
      const matchingPapers = this.allPapers.filter(paper => paper.category === categoryId);
      console.log(`Buscando papers con categoría "${categoryId}":`, matchingPapers.length);
      console.log('Papers que coinciden:', matchingPapers.map(p => ({title: p.title, category: p.category})));
      console.log('Todas las categorías de papers:', [...new Set(this.allPapers.map(p => p.category))]);
      return matchingPapers.length;
    } else {
      const matchingVideos = this.allVideos.filter(video => video.category === categoryId);
      console.log(`Buscando videos con categoría "${categoryId}":`, matchingVideos.length);
      console.log('Videos que coinciden:', matchingVideos.map(v => ({title: v.title, category: v.category})));
      console.log('Todas las categorías de videos:', [...new Set(this.allVideos.map(v => v.category))]);
      return matchingVideos.length;
    }
  }

  getCategoryCount(categoryName: string): number {
    const category = this.systemCategories.find(cat => cat.name === categoryName);
    return category ? this.getCategoryCountById(category.id) : 0;
  }

  loadPapers(): void {
    this.papersService.getPapers().subscribe({
      next: (papers: Paper[]) => {
        console.log('Papers cargados:', papers);
        console.log('Categorías de papers:', papers.map(p => ({title: p.title, category: p.category})));
        
        // Filtrar solo papers activos (status === 1) para usuarios executive y premium
        const activePapers = papers.filter(paper => paper.status === 1);
        
        this.allPapers = activePapers;
        this.filteredPapers = activePapers;
        this.featuredPapers = activePapers.filter(paper => paper.featured).slice(0, 6);
        this.updateCategoriesFilter();
        this.applyActiveFilter();
        this.dataLoaded.papers = true;
        this.checkAndActivatePendingDimension();
      },
      error: (err: any) => {
        console.error('Error al cargar papers', err);
      }
    });
  }

  loadVideos(): void {
    this.videosService.getVideos().subscribe({
      next: (videos: Video[]) => {
        this.allVideos = videos;
        this.filteredVideos = videos;
        this.featuredVideos = videos.filter(video => video.featured).slice(0, 6);
        this.updateCategoriesFilter();
        this.applyActiveFilter();
        this.dataLoaded.videos = true;
        this.checkAndActivatePendingDimension();
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error('Error al cargar videos', err);
        this.spinner.hide();
      }
    });
  }



  // Lista de categorías del sistema - SOLO PARA COLORES
  // Las categorías reales se cargan dinámicamente desde la base de datos
  systemCategories: AcademyDimension[] = [];

  private normalizeDimensions(data: any[]): AcademyDimension[] {
    if (!Array.isArray(data)) {
      return [];
    }

    const normalized: AcademyDimension[] = [];

    data.forEach((dimension: any) => {
      const id = (dimension.categoryId ?? dimension.id ?? dimension._id)?.toString();
      const name = dimension.name || dimension.title || '';
      if (!id || !name) {
        return;
      }
      const color = dimension.color || this.getDefaultCategoryColor(id);
      normalized.push({ id, name, color });
    });

    return normalized;
  }

  private getDefaultCategoryColor(categoryId?: string): string {
    const defaults: Record<string, string> = {
      '1': '#00B257',
      '2': '#9149FF',
      '3': '#1F87FC',
      '4': '#FFA300',
      '5': '#C00000',
      '6': '#0098A0',
      '7': '#FF3B47',
      '8': '#007F25'
    };

    if (!categoryId) {
      return '#999999';
    }

    return defaults[categoryId] || '#999999';
  }

  getCategoryName(categoryId: string): string {
    // Buscar primero en las dimensiones cargadas desde la base de datos
    const dimension = this.loadedDimensions.find(d => d.id === categoryId);
    if (dimension) {
      return dimension.name;
    }
    
    // Si no se encuentra, buscar en videoCategories
    const videoCategory = this.videoCategories.find(c => c.id === categoryId);
    if (videoCategory) {
      return videoCategory.name;
    }
    
    // Como último recurso, buscar en systemCategories (solo para compatibilidad)
    const systemCategory = this.systemCategories.find(c => c.id === categoryId);
    if (systemCategory) {
      return systemCategory.name;
    }
    
    return 'Sin categoría';
  }

  getCategoryColor(categoryId: string): string {
    const systemCategory = this.systemCategories.find(c => c.id === categoryId);
    return systemCategory ? systemCategory.color : '#999999';
  }

  getAdditionalCategoriesTooltip(categories: any[]): string {
    if (!categories || categories.length <= 1) {
      return '';
    }
    // Obtener todas las categorías excepto la primera
    const additionalCategories = categories.slice(1);
    // Retornar los nombres separados por comas
    return additionalCategories.map(cat => cat.name || cat).join(', ');
  }

  // Método para verificar si todos los datos están cargados y activar la dimensión pendiente
  private checkAndActivatePendingDimension(): void {
    // Si todos los datos están cargados, forzar actualización de categorías
    if (this.dataLoaded.papers && 
        this.dataLoaded.videos && 
        this.dataLoaded.categories &&
        this.dataLoaded.dimensions) {
      console.log('Todos los datos cargados, forzando actualización de categorías');
      this.updateCategoriesFilter();
      
      // Activar dimensión pendiente si existe
      if (this.pendingDimension) {
        console.log('Activando dimensión pendiente:', this.pendingDimension);
        this.activateCategoryByDimension(this.pendingDimension);
        this.pendingDimension = null; // Limpiar la dimensión pendiente
      }
    }
  }

  // Método para activar una categoría basada en la dimensión recibida
  activateCategoryByDimension(dimension: string): void {
    console.log('Intentando activar categoría para dimensión:', dimension);
    console.log('Mapeo disponible:', this.dimensionToCategoryMap);

    // Buscar la categoría correspondiente en el mapeo dinámico
    const categoryId = this.dimensionToCategoryMap[dimension];
    
    if (categoryId) {
      console.log(`Mapeo encontrado: ${dimension} -> ID: ${categoryId}`);
      
      // Activar la pestaña de lecturas por defecto
      this.activeMainTab = 'lecturas';
      console.log('Tab activo cambiado a:', this.activeMainTab);
      
      // Activar el filtro de la categoría correspondiente
      this.activeFilter = categoryId;
      console.log('Filtro activo cambiado a:', this.activeFilter);
      
      // Aplicar el filtro
      this.applyActiveFilter();
      console.log('Filtro aplicado');
      
      // Forzar actualización de paginación
      this.updatePagination();
      console.log('Paginación actualizada');
      
      console.log(`✅ Categoría activada exitosamente: ${dimension} -> ID: ${categoryId}`);
    } else {
      console.warn(`❌ No se encontró mapeo para la dimensión: ${dimension}`);
      console.log('Dimensiones disponibles:', Object.keys(this.dimensionToCategoryMap));
      // Si no se encuentra mapeo, mostrar todos los elementos
      this.activeFilter = 'todos';
      this.applyActiveFilter();
    }
  }

  getVideoThumbnail(video: Video): string {
    return video.thumbnailUrl || this.videosService.getYoutubeThumbnailUrl(video.videoUrl) || 'assets/images/course-placeholder.jpg';
  }

  getPaperCover(paper: Paper): string {
    if (paper.coverUrl) {
      // Si la coverUrl ya incluye la URL base, usarla directamente
      if (paper.coverUrl.startsWith('http')) {
        return paper.coverUrl;
      }
      // Si no, construir la URL completa sin /api
      const baseUrl = environment.apiUrlClean;
      const cleanUrl = paper.coverUrl;
      return `${baseUrl}/${cleanUrl}`;
    }
    return 'assets/images/paper-placeholder.jpg';
  }


  formatViewCount(views: number | undefined): string {
    if (!views) return '0';
    
    if (views >= 1000) {
      return (views / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    
    return views.toString();
  }

  formatDownloadCount(downloads: number | undefined): string {
    if (!downloads) return '0';
    
    if (downloads >= 1000) {
      return (downloads / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    
    return downloads.toString();
  }

  formatPaperDate(date: Date | undefined): string {
    if (!date) return 'dd/mm/aaaa';
    
    const paperDate = new Date(date);
    const day = paperDate.getDate().toString().padStart(2, '0');
    const month = (paperDate.getMonth() + 1).toString().padStart(2, '0');
    const year = paperDate.getFullYear();
    
    return `${day}/${month}/${year}`;
  }

  setActiveFilter(filter: string): void {
    this.activeFilter = filter;
    this.applyActiveFilter();
  }

  setActiveMainTab(tab: 'lecturas' | 'videos'): void {
    this.activeMainTab = tab;
    this.activeFilter = 'todos';
    this.searchTerm = '';
    this.currentPage = 1;
    this.updateCategoriesFilter();
    this.applyActiveFilter();
  }

  applyActiveFilter(): void {
    if (this.activeMainTab === 'lecturas') {
      this.applyPaperFilter();
    } else {
      this.applyVideoFilter();
    }
  }

  applyPaperFilter(): void {
    if (!this.allPapers || this.allPapers.length === 0) {
      return;
    }

    // Aplicar filtro de categoría
    if (this.activeFilter === 'todos') {
      this.filteredPapers = [...this.allPapers];
    } else if (this.activeFilter === 'destacados') {
      // Filtrar por papers destacados
      this.filteredPapers = this.allPapers.filter(paper => paper.featured);
    } else {
      // Filtrar por ID de categoría
      this.filteredPapers = this.allPapers.filter(paper => 
        paper.category === this.activeFilter
      );
    }

    // Aplicar búsqueda
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      const searchTermLower = this.searchTerm.toLowerCase().trim();
      this.filteredPapers = this.filteredPapers.filter(paper => 
        paper.title.toLowerCase().includes(searchTermLower) ||
        paper.description.toLowerCase().includes(searchTermLower) ||
        paper.author.toLowerCase().includes(searchTermLower)
      );
    }
    
    // Actualizar paginación
    this.currentPage = 1;
    this.updatePagination();
  }

  applyVideoFilter(): void {
    if (!this.allVideos || this.allVideos.length === 0) {
      return;
    }

    // Aplicar filtro de categoría
    if (this.activeFilter === 'todos') {
      this.filteredVideos = [...this.allVideos];
    } else if (this.activeFilter === 'destacados') {
      // Filtrar por videos destacados
      this.filteredVideos = this.allVideos.filter(video => video.featured);
    } else {
      // Filtrar por ID de categoría
      this.filteredVideos = this.allVideos.filter(video => 
        video.category === this.activeFilter
      );
    }

    // Aplicar búsqueda
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      const searchTermLower = this.searchTerm.toLowerCase().trim();
      this.filteredVideos = this.filteredVideos.filter(video => 
        video.title.toLowerCase().includes(searchTermLower) ||
        video.description?.toLowerCase().includes(searchTermLower)
      );
    }
    
    // Actualizar paginación
    this.currentPage = 1;
    this.updatePagination();
  }
  


  getCurrentPapers(): Paper[] {
    return this.activeMainTab === 'lecturas' ? this.paginatedItems as Paper[] : [];
  }

  getCurrentVideos(): Video[] {
    return this.activeMainTab === 'videos' ? this.paginatedItems as Video[] : [];
  }

  shouldShowFewItems(): boolean {
    return this.paginatedItems.length <= 3;
  }

  getFeaturedPapers(): Paper[] {
    return this.activeMainTab === 'lecturas' ? this.featuredPapers : [];
  }

  getFeaturedVideos(): Video[] {
    return this.activeMainTab === 'videos' ? this.featuredVideos : [];
  }

  goToDetail(id: string): void {
    if (this.activeMainTab === 'lecturas') {
      this.goToPaperDetail(id);
    } else {
      this.goToVideoDetail(id);
    }
  }

  goToPaperDetail(paperId: string): void {
    this.router.navigate(['/dashboard/academy/paper', paperId]);
  }

  goToVideoDetail(videoId: string): void {
    console.log('Navegar al video:', videoId);
    // Implementar navegación al detalle del video
  }

  buyItem(id: string): void {
    if (this.activeMainTab === 'lecturas') {
      this.buyPaper(id);
    } else {
      this.buyVideo(id);
    }
  }

  buyPaper(paperId: string): void {
    console.log('Comprar paper:', paperId);
    // Implementar compra del paper
  }

  buyVideo(videoId: string): void {
    console.log('Comprar video:', videoId);
    // Implementar compra del video
  }

  playOrDownload(id: string): void {
    if (this.activeMainTab === 'lecturas') {
      this.downloadPaper(id);
    } else {
      this.playVideo(id);
    }
  }

  downloadPaper(paperId: string): void {
    this.papersService.downloadPaper(paperId).subscribe({
      next: (response: any) => {
        // Crear URL para descargar
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'paper.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err: any) => {
        console.error('Error al descargar paper:', err);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo descargar el paper',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    });
  }

  playVideo(videoId: string): void {
    if (!videoId) {
      console.error('ID de video no válido');
      return;
    }
    
    const video = this.allVideos.find(v => v._id === videoId);
    
    if (!video) {
      this.videosService.getVideoById(videoId).subscribe({
        next: (fetchedVideo) => {
          if (fetchedVideo) {
            this.openVideoPlayer(fetchedVideo);
            this.incrementVideoViews(videoId);
          }
        },
        error: (err) => {
          console.error('Error al obtener video:', err);
        }
      });
    } else {
      this.openVideoPlayer(video);
      this.incrementVideoViews(videoId);
    }
  }
  
  private openVideoPlayer(video: Video): void {
    const embedUrl = this.videosService.getYoutubeEmbedUrl(video.videoUrl);
    if (!embedUrl) {
      Swal.fire({
        title: 'Error',
        text: 'URL de video no válida',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      return;
    }
    
    Swal.fire({
      title: video.title,
      html: `<iframe width="100%" height="400" src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`,
      width: '800px',
      showCloseButton: true,
      showConfirmButton: false,
      focusConfirm: false,
      customClass: {
        container: 'video-player-modal'
      }
    });
  }
  
  private incrementVideoViews(videoId: string): void {
    this.videosService.incrementViews(videoId).subscribe({
      next: (updatedVideo: Video) => {
        this.updateVideoViewsInUI(updatedVideo);
      },
      error: (err: any) => {
        console.error('Error al incrementar vistas', err);
      }
    });
  }
  
  private updateVideoViewsInUI(updatedVideo: Video): void {
    const updateVideoViews = (videosList: Video[]) => {
      const index = videosList.findIndex(v => v._id === updatedVideo._id);
      if (index !== -1) {
        videosList[index].views = updatedVideo.views;
      }
    };
    
    updateVideoViews(this.allVideos);
    updateVideoViews(this.featuredVideos);
    updateVideoViews(this.filteredVideos);
  }

  saveItem(item: Paper | Video): void {
    if (this.activeMainTab === 'lecturas') {
      this.savePaper(item as Paper);
    } else {
      this.saveVideo(item as Video);
    }
  }

  savePaper(paper: Paper): void {
    // Implementar funcionalidad de guardar paper
    console.log('Guardar paper:', paper._id);
  }

  saveVideo(video: Video): void {
    if (!video._id) return;

    this.spinner.show('saveVideo');
    
    if (this.isVideoSaved(video._id)) {
      this.videosService.unsaveVideo(video._id).subscribe({
        next: () => {
          this.spinner.hide('saveVideo');
          Swal.fire({
            title: 'Video quitado',
            text: 'El video ha sido quitado de tus guardados',
            icon: 'success',
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: 3000
          });
        },
        error: (err: any) => {
          this.spinner.hide('saveVideo');
          Swal.fire({
            title: 'Error',
            text: 'No se pudo quitar el video',
            icon: 'error',
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: 3000
          });
        }
      });
    } else {
      this.videosService.saveVideo(video).subscribe({
        next: () => {
          this.spinner.hide('saveVideo');
          Swal.fire({
            title: 'Video guardado',
            text: 'El video ha sido guardado correctamente',
            icon: 'success',
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: 3000
          });
        },
        error: (err: any) => {
          this.spinner.hide('saveVideo');
          Swal.fire({
            title: 'Error',
            text: 'No se pudo guardar el video',
            icon: 'error',
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: 3000
          });
        }
      });
    }
  }

  isVideoSaved(videoId: string): boolean {
    return this.videosService.isVideoSaved(videoId);
  }

  shareItem(id: string): void {
    console.log('Compartir:', id);
    // Implementar compartir
  }

  scrollLeft(element: string): void {
    const container = document.getElementById(element);
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }

  scrollRight(element: string): void {
    const container = document.getElementById(element);
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }

  // Funciones de paginación
  updatePagination(): void {
    const items = this.activeMainTab === 'lecturas' ? this.filteredPapers : this.filteredVideos;
    this.totalItems = items.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    
    // Asegurar que currentPage esté en rango válido
    if (this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }
    
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedItems = items.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  changeItemsPerPage(newItemsPerPage: number): void {
    this.itemsPerPage = newItemsPerPage;
    this.currentPage = 1;
    this.updatePagination();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);
      
      if (end - start < maxVisiblePages - 1) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  openPaperPreview(paper: Paper): void {
    if (!paper || !paper._id) {
      return;
    }

    this.showPaperPreview = true;
    this.paperPreviewData = null;

    this.papersService.getPaper(paper._id).subscribe({
      next: (response: Paper) => {
        this.paperPreviewData = {
          ...paper,
          ...response,
          authorName: (response as any)?.authorName || (paper as any)?.authorName || '',
          learningObjectives: this.ensureArray(response.learningObjectives ?? paper.learningObjectives),
          learningPoints: this.ensureArray((response as any)?.learningPoints ?? (paper as any)?.learningPoints),
          tags: this.ensureArray(response.tags ?? paper.tags)
        };
      },
      error: (err: any) => {
        console.error('Error al cargar vista previa del paper', err);
        this.paperPreviewData = {
          ...paper,
          authorName: (paper as any)?.authorName || ''
        };
      }
    });
  }

  closePaperPreview(): void {
    this.showPaperPreview = false;
    this.paperPreviewData = null;
  }

  navigateToPublicPaper(paperId: string | undefined): void {
    if (!paperId) {
      return;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/academy/paper', paperId])
    );
    window.open(url, '_blank');
  }

  private ensureArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return value.split(',').map((item: string) => item.trim()).filter(Boolean);
    }
    return [];
  }
} 